/* B2B products grid + mini PDP drawer.
 *
 * Renders from the JSON catalog emitted by snippets/frido-b2b-catalog.liquid.
 * The grid is client-rendered because each product carries several colour
 * variants with their own image gallery, which section blocks handle poorly.
 */
(function () {
  'use strict';

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /* Shared windowed-dot carousel controller, reused by the grid and the
     drawer gallery. Rebuildable, unlike the one-shot version in
     frido-b2b-carousel.js — the grid re-renders on every tab change. */
  function makeCarousel(viewport, track, dots, prev, next) {
    var pages = 1;

    function maxScroll() {
      return viewport.scrollWidth - viewport.clientWidth;
    }

    function step() {
      var first = track.children[0];
      if (!first) return viewport.clientWidth;
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    function update() {
      var max = maxScroll();
      var page = max <= 1 ? 0 : Math.round((viewport.scrollLeft / max) * (pages - 1));

      if (dots) {
        for (var i = 0; i < dots.children.length; i++) {
          dots.children[i].dataset.distance = String(Math.min(Math.abs(i - page), 3));
        }
      }
      if (prev) prev.disabled = viewport.scrollLeft <= 1;
      if (next) next.disabled = viewport.scrollLeft >= max - 1;
    }

    function measure() {
      var max = maxScroll();
      pages = max <= 1 ? 1 : Math.ceil(max / viewport.clientWidth) + 1;

      var host = viewport.closest('[data-b2b-products], .frido-b2b-drawer__panel') || viewport.parentNode;
      if (host) host.classList.toggle('frido-b2b-carousel--static', max <= 1);

      if (dots) {
        dots.textContent = '';
        if (pages > 1) {
          for (var i = 0; i < pages; i++) {
            var dot = el('button', 'frido-b2b-dot');
            dot.type = 'button';
            dot.dataset.index = String(i);
            dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
            dots.appendChild(dot);
          }
        }
      }
      update();
    }

    function scrollByPage(dir) {
      var s = step();
      var perView = Math.max(1, Math.floor(viewport.clientWidth / s));
      viewport.scrollBy({ left: dir * s * perView, behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { scrollByPage(-1); });
    if (next) next.addEventListener('click', function () { scrollByPage(1); });
    if (dots) {
      dots.addEventListener('click', function (e) {
        var dot = e.target.closest('.frido-b2b-dot');
        if (!dot) return;
        var i = Number(dot.dataset.index);
        viewport.scrollTo({ left: pages < 2 ? 0 : (i / (pages - 1)) * maxScroll(), behavior: 'smooth' });
      });
    }

    var ticking = false;
    viewport.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { update(); ticking = false; });
    }, { passive: true });

    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(viewport);

    return { measure: measure, reset: function () { viewport.scrollLeft = 0; measure(); } };
  }

  function initProducts(root) {
    if (root.dataset.b2bProductsReady === 'true') return;
    root.dataset.b2bProductsReady = 'true';

    var dataNode = root.querySelector('[data-b2b-catalog]');
    if (!dataNode) return;

    var catalog;
    try {
      catalog = JSON.parse(dataNode.textContent);
    } catch (error) {
      console.error('[frido-b2b] Catalog JSON is invalid', error);
      return;
    }

    var groups = (catalog && catalog.groups) || [];
    if (!groups.length) return;

    var starIcon = root.dataset.starIcon || '';

    /* Star + score + review count. Shared so the card badge and the mini PDP
       stay in sync; only the wrapper's styling differs between the two. */
    function fillRating(container, product) {
      if (starIcon) {
        var star = el('img', 'frido-b2b-rating__star');
        star.src = starIcon;
        star.alt = '';
        star.width = 12;
        star.height = 12;
        container.appendChild(star);
      }
      container.appendChild(el('strong', null, product.rating));
      if (product.review_count) {
        container.appendChild(el('span', null, '(' + product.review_count + '+)'));
      }
    }

    var tabsWrap = root.querySelector('[data-products-tabs]');
    var viewport = root.querySelector('[data-products-viewport]');
    var track = root.querySelector('[data-products-track]');
    var grid = makeCarousel(
      viewport,
      track,
      root.querySelector('[data-products-dots]'),
      root.querySelector('[data-products-prev]'),
      root.querySelector('[data-products-next]')
    );

    /* ---------- Cards ---------- */

    function firstImage(product, variantIndex) {
      var variant = product.variants[variantIndex || 0];
      return (variant && variant.images && variant.images[0]) || '';
    }

    function buildCard(product, index) {
      var card = el('li', 'frido-b2b-card');
      card.tabIndex = 0;
      card.setAttribute('role', 'button');
      card.setAttribute('aria-label', product.name);
      card.dataset.index = String(index);

      var media = el('div', 'frido-b2b-card__media');
      var img = el('img');
      img.src = firstImage(product, 0);
      img.alt = product.name;
      img.loading = 'lazy';
      img.decoding = 'async';
      media.appendChild(img);

      if (product.rating) {
        var badge = el('div', 'frido-b2b-card__rating');
        fillRating(badge, product);
        media.appendChild(badge);
      }
      card.appendChild(media);

      if (product.tags && product.tags.length) {
        var tags = el('div', 'frido-b2b-card__tags');
        product.tags.slice(0, 2).forEach(function (tag) {
          tags.appendChild(el('span', 'frido-b2b-card__tag', tag));
        });
        card.appendChild(tags);
      }

      card.appendChild(el('p', 'frido-b2b-card__title', product.name));

      if (product.variants.length > 1) {
        var swatches = el('div', 'frido-b2b-card__swatches');
        product.variants.forEach(function (variant, vi) {
          var swatch = el('button', 'frido-b2b-card__swatch');
          swatch.type = 'button';
          swatch.title = variant.label;
          swatch.setAttribute('aria-label', variant.label);
          swatch.setAttribute('aria-pressed', vi === 0 ? 'true' : 'false');
          if (variant.hex) swatch.style.background = variant.hex;
          else if (variant.images[0]) swatch.style.backgroundImage = 'url("' + variant.images[0] + '")';

          swatch.addEventListener('click', function (event) {
            /* Swapping colour shouldn't also open the PDP. */
            event.stopPropagation();
            img.src = firstImage(product, vi);
            swatches.querySelectorAll('.frido-b2b-card__swatch').forEach(function (other) {
              other.setAttribute('aria-pressed', String(other === swatch));
            });
          });
          swatches.appendChild(swatch);
        });
        card.appendChild(swatches);
      }

      card.addEventListener('click', function () { openDrawer(product); });
      card.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openDrawer(product);
        }
      });

      return card;
    }

    function renderGroup(group) {
      track.textContent = '';
      group.items.forEach(function (product, index) {
        track.appendChild(buildCard(product, index));
      });
      grid.reset();
    }

    /* ---------- Tabs ---------- */

    groups.forEach(function (group, index) {
      var tab = el('button', 'frido-b2b-tab');
      tab.type = 'button';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', index === 0 ? 'true' : 'false');

      var icon = el('span', 'frido-b2b-tab__icon');
      if (group.icon) {
        var iconImg = el('img');
        iconImg.src = group.icon;
        iconImg.alt = '';
        iconImg.loading = 'lazy';
        icon.appendChild(iconImg);
      }
      tab.appendChild(icon);
      tab.appendChild(el('span', null, group.label));

      tab.addEventListener('click', function () {
        tabsWrap.querySelectorAll('.frido-b2b-tab').forEach(function (other) {
          other.setAttribute('aria-selected', String(other === tab));
        });
        renderGroup(group);
      });

      tabsWrap.appendChild(tab);
    });

    renderGroup(groups[0]);

    /* ---------- Mini PDP ---------- */

    var drawer = root.querySelector('[data-products-drawer]');
    var slidesWrap = drawer.querySelector('[data-drawer-slides]');
    var gallery = makeCarousel(
      drawer.querySelector('[data-drawer-gallery]'),
      slidesWrap,
      drawer.querySelector('[data-drawer-dots]'),
      drawer.querySelector('[data-drawer-prev]'),
      drawer.querySelector('[data-drawer-next]')
    );

    var current = null;
    var lastFocus = null;

    function renderGallery(product, variantIndex) {
      var variant = product.variants[variantIndex];
      slidesWrap.textContent = '';

      /* Video leads the gallery. It's keyed off the product rather than the
         variant, so it stays put across colour swaps. */
      if (product.video) {
        var videoSlide = el('li', 'frido-b2b-drawer__slide frido-b2b-drawer__slide--video');
        var video = document.createElement('video');
        video.src = product.video;
        video.muted = true;
        video.loop = true;
        video.autoplay = true;
        video.controls = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.preload = 'metadata';
        if (variant.images && variant.images[0]) video.poster = variant.images[0];
        videoSlide.appendChild(video);
        slidesWrap.appendChild(videoSlide);
      }

      (variant.images || []).forEach(function (src) {
        var slide = el('li', 'frido-b2b-drawer__slide');
        var img = el('img');
        img.src = src;
        img.alt = product.name + ' — ' + variant.label;
        img.loading = 'lazy';
        slide.appendChild(img);
        slidesWrap.appendChild(slide);
      });

      gallery.reset();
    }

    function selectVariant(product, variantIndex) {
      renderGallery(product, variantIndex);
      var nameNode = drawer.querySelector('[data-drawer-color-name]');
      if (nameNode) nameNode.textContent = product.variants[variantIndex].label;
      drawer.querySelectorAll('.frido-b2b-drawer__swatch').forEach(function (swatch, i) {
        swatch.setAttribute('aria-pressed', String(i === variantIndex));
      });
    }

    function openDrawer(product) {
      current = product;
      lastFocus = document.activeElement;

      drawer.querySelector('[data-drawer-title]').textContent = product.name;

      var tags = drawer.querySelector('[data-drawer-tags]');
      tags.textContent = '';
      (product.tags || []).slice(0, 2).forEach(function (tag) {
        tags.appendChild(el('span', 'frido-b2b-drawer__tag', tag));
      });

      /* Rating only — the "N added to cart" social-proof block is omitted. */
      var rating = drawer.querySelector('[data-drawer-rating]');
      rating.textContent = '';
      if (product.rating) fillRating(rating, product);

      drawer.querySelector('[data-drawer-desc]').textContent = product.description || '';

      var colors = drawer.querySelector('[data-drawer-colors]');
      var swatches = drawer.querySelector('[data-drawer-swatches]');
      swatches.textContent = '';
      if (product.variants.length > 1) {
        colors.hidden = false;
        product.variants.forEach(function (variant, vi) {
          var swatch = el('button', 'frido-b2b-drawer__swatch');
          swatch.type = 'button';
          swatch.setAttribute('aria-label', variant.label);
          var img = el('img');
          img.src = variant.images[0];
          img.alt = variant.label;
          img.loading = 'lazy';
          swatch.appendChild(img);
          swatch.addEventListener('click', function () { selectVariant(product, vi); });
          swatches.appendChild(swatch);
        });
      } else {
        colors.hidden = true;
      }

      selectVariant(product, 0);

      drawer.hidden = false;
      document.body.classList.add('frido-b2b-drawer-open');
      window.requestAnimationFrame(function () {
        drawer.classList.add('frido-b2b-drawer--open');
        gallery.measure();
      });

      var close = drawer.querySelector('.frido-b2b-drawer__close');
      if (close) close.focus();
    }

    function closeDrawer() {
      drawer.classList.remove('frido-b2b-drawer--open');
      document.body.classList.remove('frido-b2b-drawer-open');
      window.setTimeout(function () { drawer.hidden = true; }, 300);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    drawer.querySelectorAll('[data-drawer-close]').forEach(function (node) {
      node.addEventListener('click', closeDrawer);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !drawer.hidden) closeDrawer();
    });

    /* Any [data-open-pdp="<product name>"] anywhere on the page opens the mini
       PDP — used by the product chips on the On Socials videos. */
    var byName = {};
    groups.forEach(function (group) {
      group.items.forEach(function (product) { byName[product.name] = product; });
    });

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-open-pdp]');
      if (!trigger) return;
      var product = byName[trigger.dataset.openPdp];
      if (!product) {
        console.warn('[frido-b2b] No catalog product named:', trigger.dataset.openPdp);
        return;
      }
      event.preventDefault();
      openDrawer(product);
    });

    /* Quote button: close, jump to the enquiry form, and tell the form which
       product prompted it so the lead carries that context. */
    var quote = drawer.querySelector('[data-drawer-quote]');
    if (quote) {
      quote.addEventListener('click', function () {
        var selector = root.dataset.quoteTarget || '#frido-b2b-form-hero';
        var form = document.querySelector(selector);
        var name = current ? current.name : '';

        closeDrawer();

        if (!form) {
          console.warn('[frido-b2b] Quote target not found:', selector);
          return;
        }

        var interest = form.querySelector('[name="product_interest"]');
        if (interest) interest.value = name;

        window.setTimeout(function () {
          form.scrollIntoView({ behavior: 'smooth', block: 'center' });
          var firstField = form.querySelector('input:not([type="hidden"]):not([tabindex="-1"])');
          if (firstField) {
            window.setTimeout(function () { firstField.focus({ preventScroll: true }); }, 500);
          }
        }, 320);
      });
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-b2b-products]').forEach(initProducts);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
})();
